// The single frontend rule for matching a worker to an open shift — the client mirror of the
// server's Constraints::Evaluator + EffectiveWorker (the WorkerTerm covering the shift's date).
// It renders the drag drop ring (schedule-drag-service) and the "Eligible shifts" ring
// (worker-availability); the server force gate stays the authoritative guard, so this affordance may
// only UNDER-scope, never let invalid data in. Keep the two in sync via shift-eligibility.contract.
//
// Role matching (WORK-6513) honors the same EligibilityRule matrix the engine + candidate feed
// apply: beyond an exact same-role match (and the charge-moonlight special case), a cross-role
// worker is eligible when `eligibleRolePairs` — the run service line's non-ineligible
// "workerRole->slotRole" pairs — contains the pair keyed off the covering-term role. This includes
// X->CHG pairs (a mapped cross-role charge rule); absent such a rule, a charge slot falls to the
// charge-moonlight branch. The Set defaults empty, so a missing payload can only UNDER-scope
// (block valid cross-role drops), never admit invalid data.
import type {
  ScheduleDropValidityType,
  ScheduleShiftType,
  ScheduleTeamMemberType,
} from '@load-balancing/types';
// Sibling import (not the @load-balancing/services barrel) — the barrel re-exports this file, so
// going through it would be a self-referential cycle.
import { findCoveringTerm } from './team-review-roster';

/**
 * The key format for the cross-role eligibility set: `"workerRole->slotRole"` role abbreviations.
 * Owned here so the FE lookup can never disagree with itself, and so the server builder's emitted
 * strings (schedule_grid/builder.rb#eligible_role_pairs) can be asserted byte-identical across the
 * language boundary (shift-eligibility contract). Mirrors the server's `"#{worker}->#{slot}"`.
 */
export const rolePairKey = (workerRole: string, slotRole: string): string =>
  `${workerRole}->${slotRole}`;

/**
 * Whether the dragged worker may take the given open slot — the FE's Evaluator. Per WORK-6399, drag
 * is a free-form, power-user action, so FTE/hours/preference no longer block the drop — they're
 * forced past server-side and surface as warnings (the over-FTE "Needs Attention" card + the shift's
 * assignedReasons) after the assign. The gates kept here mirror the server's force gate
 * (NON_OVERRIDABLE_KEYS), all resolved from the term covering the slot's date (the FE's
 * EffectiveWorker, {@link findCoveringTerm}): worker-active-on-the-date (not terminated / on leave),
 * role-match against the worker's role ON THE SLOT'S DATE (so a role change across terms is honored;
 * a charge slot also takes a charge-eligible worker in ANY unit — a cross-unit charge moonlight is a
 * drag-only power move and this is a drag affordance, so the panel-only home-unit restriction is not
 * gated here; keyed off the shift's data-driven `isChargeRole`, never a role
 * string), and time overlap — a worker can't be double-booked onto a
 * block they already hold. `heldDaySections` is the set of `${dayKey}-${shiftTypeSection}` the worker
 * already holds (open slots carry no start/end time, so day+band is the client proxy the codebase
 * uses for availability; the server re-checks the true time overlap). Keeping these client-side stops
 * the green drop ring over-promising and then reverting.
 *
 * Per-slot resolution (WORK-6528): role / charge_eligible / active can change between a worker's
 * terms, and the server reads them from the term STRICTLY covering the shift's date
 * (`WorkerTerm.effective_on`, no neighbouring-term fallback). The affordance reads them the same way
 * from the term covering `slotDate` (ISO8601 'YYYY-MM-DD'), not the flat point-in-time member fields.
 * Fails closed: a missing `slotDate` / no covering term ⇒ the per-slot gates fall back to the server.
 */
export function computeOpenSlotDropValidity(
  worker: ScheduleTeamMemberType | null,
  shift: ScheduleShiftType,
  heldDaySections: ReadonlySet<string> = new Set(),
  slotDate?: string,
  // "workerRole->slotRole" pairs the EligibilityRule matrix marks non-ineligible (WORK-6513); empty
  // ⇒ same-role + charge only (today's under-scope, and the fail-safe). See rolePairKey.
  eligibleRolePairs: ReadonlySet<string> = new Set()
): ScheduleDropValidityType {
  if (!worker) return { valid: false, reason: 'Unavailable' };

  // The term covering the slot's date is the FE's EffectiveWorker — the per-slot gates read role /
  // active / charge from it. Strict covering (no neighbouring-term fallback), like the server.
  const coveringTerm =
    slotDate !== undefined
      ? findCoveringTerm(worker.terms, slotDate)
      : undefined;

  // A worker whose covering term marks them inactive (terminated / on leave) can't take ANY shift
  // that day — mirrors the server's non-overridable `worker_inactive` gate. A missing covering term
  // (a coverage gap, prevented for current/future dates by the term write path) is left to the
  // server and the charge/role checks below, so mock/unpopulated-terms callers keep working.
  if (coveringTerm && !coveringTerm.active) {
    return { valid: false, reason: 'Worker inactive on this date' };
  }

  const chargeEligibleForSlot = coveringTerm?.chargeEligible ?? false;
  // Charge moonlight: a charge-eligible non-charge worker may fill a CHG slot. Home-unit is NOT
  // gated here — this rule drives drag affordances, and a drag force-lifts the cross-unit charge
  // split, so a charge-eligible worker is draggable into a CHG slot in any unit. The panel Assign
  // stays home-unit-only via the server candidate feed (charge_moonlight_unit_mismatch).
  const chargeMoonlight =
    (shift.isChargeRole ?? false) && chargeEligibleForSlot;
  // Match the slot against the worker's role ON THE SLOT'S DATE (the covering term's role), which
  // can differ from the flat current-term role when the role changes across terms — mirrors the
  // server gate. Falls back to the flat role when the covering term / its role is unavailable.
  const roleForSlot = coveringTerm?.role ?? worker.role;
  // Cross-role eligibility (WORK-6513): the covering-term role → slot role pair is in the matrix.
  // Includes X->CHG — RoleIneligible honors a non-ineligible X->CHG rule for a non-CHG worker on the
  // charge branch server-side (role_ineligible.rb), so the ring greens those drops too; absent such a
  // rule a charge slot falls to the charge-moonlight branch below. Unit-agnostic here, like the
  // server rule path (cross-unit is a separate, liftable gate).
  const crossRoleEligible = eligibleRolePairs.has(
    rolePairKey(roleForSlot, shift.role)
  );
  const roleMatch =
    roleForSlot === shift.role || crossRoleEligible || chargeMoonlight;
  if (!roleMatch) {
    // The roster shows the worker's CURRENT role, so if that would match but their role on the
    // slot's date doesn't, name the cause (a role change) rather than a bare "Role mismatch".
    const reason =
      worker.role === shift.role
        ? 'Role changes on this date'
        : 'Role mismatch';
    return { valid: false, reason };
  }

  if (heldDaySections.has(`${shift.dayKey}-${shift.shiftTypeSection}`)) {
    return { valid: false, reason: 'Already assigned' };
  }

  return { valid: true };
}

/**
 * The `${dayKey}-${shiftTypeSection}` bands the dragged `worker` already holds — the held set fed to
 * {@link computeOpenSlotDropValidity} (open cells carry no start/end time, so day+band is the client
 * proxy for a time conflict). Matches by uuid (`assignedWorkerId`) first, with a name fallback for
 * rows that carry no `assignedWorkerId` (e.g. PTO blocks); names collide where ids don't, so the
 * fallback is best-effort and the server stays authoritative on the true time overlap. Excludes the
 * drag's own source shift (`fromShiftId`) so moving a worker's shift to another open cell in the same
 * band isn't flagged as a self-conflict.
 *
 * The key omits `weekKey` ON PURPOSE: callers pass a single active week's shifts (the grid renders
 * one Sun–Sat week at a time), so `dayKey`+band is unambiguous within that scope. If a caller ever
 * feeds multiple weeks into `shifts`, add `weekKey` to the key — otherwise the same `wed-day` band
 * would false-block across every week.
 */
export function heldDaySectionsFor(
  shifts: ScheduleShiftType[],
  worker: ScheduleTeamMemberType | null,
  fromShiftId: string | undefined
): Set<string> {
  if (!worker) return new Set();
  return new Set(
    shifts
      .filter(
        (entry) =>
          entry.id !== fromShiftId &&
          (entry.assignedWorkerId === worker.id ||
            (!entry.assignedWorkerId && entry.workerName === worker.name))
      )
      .map((entry) => `${entry.dayKey}-${entry.shiftTypeSection}`)
  );
}
